<script>
    import { mapActions } from "vuex";
    import ThePagination from "@main/packages/ThePagination";
    import ProductReviewsCard from "@/components/product/ProductReviewsCard";

    export default {
        name: "ProductReviews",
        components: {
            ThePagination,
            ProductReviewsCard,
        },
        props: {
            reviews: Object,
            product: Object,
            isArrow: {
                type: Boolean,
                default: true,
            },
        },
        data() {
            return {
                isLoading: false,
            };
        },
        methods: {
            ...mapActions("Product", ["getProductReview"]),
            scrollTo() {
                const el = this.$refs.reviews;
                const top = el.getBoundingClientRect().top + window.scrollY - 55;
                window.scrollTo({
                    top,
                    behavior: "smooth",
                });
            },
            onChangePage(page) {
                this.isLoading = true;
                this.scrollTo();
                this.getProductReview({ id: this.product.id, page }).then(() => {
                    this.isLoading = false;
                });
            },
            getAverageRatingText(rating) {
                const ratingArray = String(rating).split(".");
                return ratingArray[ratingArray.length - 1];
            },
        },
    };
</script>

<template>
    <div class="the-product-reviews" ref="reviews">
        <div class="the-product-reviews__header">
            <h2 class="the-product-reviews__title">Отзывы</h2>

            <svg
                class="the-product-reviews__star"
                width="60"
                height="60"
                viewBox="0 0 60 60"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="m30 0 7.58 19.56 20.95 1.17-16.26 13.26 5.36 20.28L30 42.9 12.37 54.27l5.36-20.28L1.47 20.73l20.95-1.17L30 0Z"
                />
            </svg>

            <div class="the-product-reviews__rating">
                <template v-if="product.averageRating > 0">
                    <span>{{ product.averageRating }}</span>
                    <span class="the-product-reviews__rating-text">
                        &nbsp;{{
                            getAverageRatingText(product.averageRating)
                                | formatCase(["балл", "балла", "баллов"])
                        }}
                    </span>
                </template>
            </div>

            <span class="the-product-reviews__number">
                <span class="the-product-reviews__divider">/</span>{{ reviews.total }}
                {{ reviews.total | formatCase(["отзыв", "отзыва", "отзывов"]) }}</span
            >
        </div>

        <div v-show="isLoading" class="the-product-reviews__loader">
            <preloader :show="true" lg-size is-color></preloader>
        </div>

        <div class="the-product-reviews__content" :class="{ 'is-loading': isLoading }">
            <div
                class="the-product-reviews__card"
                v-for="review in reviews.items"
                :key="review.id"
            >
                <product-reviews-card :review="review"></product-reviews-card>
            </div>
        </div>

        <div v-if="reviews.lastPage > 1" class="the-product-reviews__pagination">
            <the-pagination
                :current="reviews.number"
                :last="reviews.lastPage"
                :is-arrow="isArrow"
                @on-change="onChangePage"
            ></the-pagination>
        </div>
    </div>
</template>

<style lang="scss">
    .the-product-reviews {
        position: relative;
    }

    .the-product-reviews__loader {
        position: absolute;
        top: 120px;
        left: 0;
        right: 0;
        height: 300px;
        @include bp($bp-desktop-sm) {
            top: 240px;
        }
    }

    .the-product-reviews__header {
        position: relative;
        display: flex;
        align-items: center;
        margin-bottom: 40px;
        @include bp($bp-desktop-sm) {
            margin-bottom: 80px;
        }
    }

    .the-product-reviews__content {
        transition: opacity 0.2s ease-in;
        &.is-loading {
            opacity: 0.5;
        }
    }

    .the-product-reviews__card {
        &:not(:last-child) {
            margin-bottom: 60px;
        }
        @include bp($bp-desktop-sm) {
            &:not(:last-child) {
                margin-bottom: 80px;
            }
        }
    }

    .the-product-reviews__title {
        position: relative;
        z-index: 1;
        margin-right: -10px;
        font-size: 16px;
        font-weight: var(--product-reviews-title-weight);
        line-height: 22px;
        @include bp($bp-desktop-sm) {
            margin-right: 20px;
            font-size: 22px;
            line-height: 30px;
        }
    }

    .the-product-reviews__star {
        fill: var(--product-reviews-star-color);
        @include box(60px);
    }

    .the-product-reviews__rating {
        position: relative;
        margin-right: 5px;
        margin-left: -30px;
        font-size: 12px;
        line-height: 18px;
        transform: translateX(-50%);

        span {
            position: relative;
            z-index: 1;
        }
        @include bp($bp-desktop-sm) {
            font-size: 16px;
            font-weight: 600;
            line-height: 22px;
            transform: none;
        }
    }

    .the-product-reviews__rating-text {
        display: none;
        @include bp($bp-desktop-sm) {
            display: inline-block;
        }
    }

    .the-product-reviews__number {
        position: relative;
        z-index: 1;
        font-size: 12px;
        line-height: 18px;
        @include bp($bp-desktop-sm) {
            font-size: 16px;
            line-height: 22px;
        }
    }

    .the-product-reviews__divider {
        display: none;
        margin-right: 5px;
        @include bp($bp-desktop-sm) {
            display: inline-block;
        }
    }

    .the-product-reviews__pagination {
        display: flex;
        justify-content: center;
        margin-top: 40px;
        @include bp($bp-desktop-sm) {
            justify-content: flex-start;
        }
    }
</style>
